import { createUseStyles } from '@glu/theming';
import PropTypes from 'prop-types';
import React, { useRef, useEffect } from 'react';
import {
  Button, PRIMARY, SECONDARY, SMALL
} from '@glu/buttons-react';
import locale from '@glu/locale';
import styles from './Actions.styles';
import './themeDefaults';

const useStyles = createUseStyles(styles);

const Actions = ({
  btnClearEnabled,
  btnDoneEnabled,
  btnSelectAllEnabled,
  focused,
  onClear,
  applySelection,
  onSelectAll,
  options,
  selection,
  setSelection
}) => {
  const classes = useStyles();

  const handleClear = () => {
    setSelection(selection.filter(({ disabled }) => disabled));
    onClear();
  };
  const handleSelectAll = () => {
    setSelection([
      ...options.filter(({ disabled }) => !disabled),
      ...selection.filter(({ disabled }) => disabled)
    ]);
    onSelectAll();
  };

  const buttonRef = useRef();

  useEffect(() => {
    if (focused) { buttonRef.current.querySelector('button')?.focus(); }
  });

  return (
    <div className={classes.root} ref={buttonRef}>
      {btnDoneEnabled && (
        <Button
          className={classes.button}
          data-qa="button-done"
          onClick={() => { applySelection(selection); }}
          size={SMALL}
          text={locale.get('done')}
          type="button"
          variant={PRIMARY}
        />
      )}
      {(btnSelectAllEnabled || btnClearEnabled) && (
        <>
          {btnSelectAllEnabled && (
            <Button
              className={classes.button}
              data-qa="button-select-all"
              onClick={handleSelectAll}
              size={SMALL}
              text={locale.get('selectall')}
              type="button"
              variant={SECONDARY}
            />
          )}
          {btnClearEnabled && (
            <Button
              className={classes.button}
              data-qa="button-clear"
              onClick={handleClear}
              size={SMALL}
              text={locale.get('clear')}
              type="button"
              variant={SECONDARY}
            />
          )}
        </>
      )}
    </div>
  );
};

Actions.propTypes = {
  /** Toggle to enable clear button */
  btnClearEnabled: PropTypes.bool.isRequired,

  /** Toggle if done button is visible. Actively selecting done is not required. */
  btnDoneEnabled: PropTypes.bool.isRequired,

  /** Toggle to enable select all button */
  btnSelectAllEnabled: PropTypes.bool.isRequired,

  /** Whether to make this option the active element */
  focused: PropTypes.bool.isRequired,

  /** Function to be called when the user clears the selection */
  onClear: PropTypes.func.isRequired,

  /** Function to be called when the user is done */
  applySelection: PropTypes.func.isRequired,

  /** Function to be called when the user selects all the options on the list */
  onSelectAll: PropTypes.func.isRequired,

  /** Object representing options provided to the dropdown panel */
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,

  /** Object representing the state of the current selection */
  selection: PropTypes.arrayOf(PropTypes.shape({})).isRequired,

  /** Function to set state of selection object */
  setSelection: PropTypes.func.isRequired
};

export default Actions;
